/**
 * 营销站 / 编辑器共用的登录会话持久化工具。
 *
 * - 使用同一个 `localStorage` key（同源下跨 tab、跨子应用共享）
 * - 通过原生 `storage` 事件在跨 tab 或不同入口（营销 / 编辑器）之间同步会话
 *
 * 注意：仅存在会话不等于 Nitro 代理已鉴权。营销站请求 Animock 的 Nitro 路由使用
 * `animockApiFetch`，只转发 HTTP 请求头中的 `ba-user-token`，不会读取本模块的
 * `localStorage`。浏览器侧须通过 `useAnimockMarketingFetch`（或等价逻辑）把
 * `readAuthSession().token` 写入请求头，否则会话在本地仍「像已登录」而接口返回 401。
 */

/** 统一的 localStorage key。 */
export const AUTH_SESSION_STORAGE_KEY = 'imockup:auth-session'

/** 会话变更时派发到 `globalThis` 的自定义事件名（用于同 tab 内不同组件同步）。 */
export const AUTH_SESSION_EVENT = 'imockup:auth-session-change'

export interface AuthSession {
  token: string
  refreshToken?: string
  email: string
  username?: string
  nickname?: string
  avatar?: string
  createdAt?: string
  isSubscribed?: boolean
  /**
   * 会员档位（来自营销 Nitro `/api/entitlement/current`）。
   */
  membershipTier?: 'none' | 'monthly' | 'yearly' | 'lifetime'
  /**
   * 由档位推导的 plan_code 展示用（同上接口）。
   */
  membershipPlanCode?: string
  /**
   * 会员生效时间（秒时间戳；当前接口未提供时为 0）。
   */
  membershipStartedAt?: number
  /**
   * 会员到期时间（秒时间戳，0=永久）。
   */
  membershipExpiresAt?: number
  /**
   * 当前订阅号（若存在未终态订阅合约），用于“订阅详情”入口直达。
   */
  currentSubscriptionNo?: string
  /**
   * 免密登录有效天数（7 / 14 / 30）。未勾选「记住密码」时可不写或写 0，
   * 此时仅依赖浏览器会话时长，关闭浏览器再次打开读取到 session 即视为过期并清除。
   */
  keepDays?: number
  /**
   * 会话过期的 ISO 时间戳。若存在且已过期，`readAuthSession` 将返回 `null` 并自动清除存储。
   */
  expiresAt?: string
}

/**
 * 判断会话是否已过期。
 *
 * - 有 `expiresAt` 则按其比对当前时间
 * - 无 `expiresAt` 则视为有效（兼容旧会话）
 *
 * @param session - 待判断会话
 */
function isSessionExpired(session: AuthSession | null): boolean {
  if (!session) return false
  if (!session.expiresAt) return false
  const expires = Date.parse(session.expiresAt)
  if (Number.isNaN(expires)) return false
  return Date.now() >= expires
}

/**
 * 安全读取 localStorage 中的会话（SSR 下返回 null）。
 */
export function readAuthSession(): AuthSession | null {
  if (globalThis.window === undefined || !globalThis.localStorage) return null
  try {
    const raw = globalThis.localStorage.getItem(AUTH_SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AuthSession
    if (!parsed || typeof parsed !== 'object' || !parsed.token) return null
    if (isSessionExpired(parsed)) {
      globalThis.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/**
 * 写入会话到 localStorage，并派发同 tab 内的同步事件。
 *
 * @param session - 目标会话；传入 null 表示清除
 */
export function writeAuthSession(session: AuthSession | null): void {
  if (globalThis.window === undefined || !globalThis.localStorage) return
  try {
    if (session) {
      globalThis.localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session))
    } else {
      globalThis.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
    }
    globalThis.dispatchEvent(new CustomEvent<AuthSession | null>(AUTH_SESSION_EVENT, { detail: session }))
  } catch {
    /* ignore quota/serialize errors */
  }
}

/**
 * 订阅会话变更（同 tab 自定义事件 + 跨 tab storage 事件）。
 *
 * @param handler - 会话变更回调；收到 null 表示清除
 * @returns 取消订阅函数
 */
export function subscribeAuthSession(handler: (session: AuthSession | null) => void): () => void {
  if (globalThis.window === undefined) return () => undefined

  const onCustom = (ev: Event): void => {
    const detail = (ev as CustomEvent<AuthSession | null>).detail ?? null
    handler(detail)
  }
  const onStorage = (ev: StorageEvent): void => {
    if (ev.key !== AUTH_SESSION_STORAGE_KEY) return
    if (!ev.newValue) {
      handler(null)
      return
    }
    try {
      handler(JSON.parse(ev.newValue) as AuthSession)
    } catch {
      handler(null)
    }
  }
  globalThis.addEventListener(AUTH_SESSION_EVENT, onCustom as EventListener)
  globalThis.addEventListener('storage', onStorage)
  return () => {
    globalThis.removeEventListener(AUTH_SESSION_EVENT, onCustom as EventListener)
    globalThis.removeEventListener('storage', onStorage)
  }
}
